import { useState } from 'react'
import { fbt } from 'fbtee'
import { cn } from '@/lib/utils'

interface BeaconLogoProps {
  offline?: boolean
  /** Hour (0-23) and minute (0-59) for the clock hands. */
  hours?: number
  minutes?: number
  /** Tint the clock hands amber to signal an overridden time. */
  clockPinned?: boolean
  /** Short day label (e.g. "THU") shown as a badge on the logo. */
  dayLabel?: string
  className?: string
  onClick?: () => void
}

const COLORS = {
  online: { ripple: '#818CF8', body: '#6366F1', core: '#67E8F9' },
  offline: { ripple: '#71717a', body: '#52525b', core: '#a1a1aa' },
}

const CX = 64
const CY = 50
const HOUR_LEN = 28
const MINUTE_LEN = 40
const BG = '#0a0a0f'
const HAND_W = 4
const BORDER_W = 9

// Unwraps a 0-359 angle into an ever-increasing one so CSS always rotates the
// short way forward — without it, 359 -> 1 animates a full turn backwards.
function useForwardRotation(deg: number) {
  const [prev, setPrev] = useState(deg)
  const [cumulative, setCumulative] = useState(deg)

  if (prev !== deg) {
    const delta = (((deg - prev) % 360) + 360) % 360
    setPrev(deg)
    setCumulative(cumulative + delta)
    return cumulative + delta
  }

  return cumulative
}

export function BeaconLogo({ offline, hours, minutes, clockPinned, dayLabel, className, onClick }: BeaconLogoProps) {
  const c = offline ? COLORS.offline : COLORS.online
  const id = offline ? 'off' : 'on'
  const showHands = hours != null && minutes != null
  const handColor = clockPinned ? '#fbbf24' : c.core

  const rawMinuteDeg = showHands ? minutes * 6 : 0
  const rawHourDeg = showHands ? (hours % 12) * 30 + minutes * 0.5 : 0
  const minuteDeg = useForwardRotation(rawMinuteDeg)
  const hourDeg = useForwardRotation(rawHourDeg)

  return (
    <svg
      className={cn('rounded-full overflow-visible', className)}
      viewBox="19 5 90 90"
      role="img"
      aria-label={
        offline
          ? fbt('Faro offline', 'Accessible label for the app logo while the device is offline')
          : clockPinned
            ? fbt('Faro — clock pinned', 'Accessible label for the app logo while the debug clock override is active')
            : fbt('Faro', 'Accessible label for the app logo')
      }
      onClick={onClick}
    >
      <defs>
        <clipPath id={`c-${id}`}><circle cx={CX} cy={CY} r="45"/></clipPath>
        <mask id={`m-${id}`}>
          <rect x="19" y="5" width="90" height="90" fill="white"/>
          <path d="M64 37 C59 37 55 41 55 46 L48 91 H80 L73 46 C73 41 69 37 64 37 Z" fill="black"/>
        </mask>
      </defs>
      <g clipPath={`url(#c-${id})`}>
        <circle cx={CX} cy={CY} r="45" fill={BG}/>
        <g mask={`url(#m-${id})`}>
          <circle cx={CX} cy={CY} r="17" stroke={c.ripple} strokeWidth="4" fill="none" opacity="0.85"/>
          <circle cx={CX} cy={CY} r="27" stroke={c.ripple} strokeWidth="4" fill="none" opacity="0.55"/>
          <circle cx={CX} cy={CY} r="37" stroke={c.ripple} strokeWidth="4" fill="none" opacity="0.3"/>
        </g>
        <path d="M64 40 C60 40 58 43 58 48 L51 88 H77 L70 48 C70 43 68 40 64 40 Z" fill={c.body}/>
        {dayLabel && <>
          <text x={CX} y="88" textAnchor="middle" fill={BG} stroke={BG} strokeWidth={BORDER_W} strokeLinejoin="round" paintOrder="stroke" fontSize="20" fontWeight="800" fontFamily="system-ui, sans-serif" letterSpacing="1">{dayLabel}</text>
          <text x={CX} y="88" textAnchor="middle" fill={handColor} fontSize="20" fontWeight="800" fontFamily="system-ui, sans-serif" letterSpacing="1">{dayLabel}</text>
        </>}
        {showHands && <>
          <line x1={CX} y1={CY} x2={CX} y2={CY - HOUR_LEN} stroke={BG} strokeWidth={BORDER_W} strokeLinecap="round" style={{ transform: `rotate(${hourDeg}deg)`, transformOrigin: `${CX}px ${CY}px`, transition: 'transform 0.6s cubic-bezier(.4,0,.2,1)' }}/>
          <line x1={CX} y1={CY} x2={CX} y2={CY - MINUTE_LEN} stroke={BG} strokeWidth={BORDER_W} strokeLinecap="round" style={{ transform: `rotate(${minuteDeg}deg)`, transformOrigin: `${CX}px ${CY}px`, transition: 'transform 0.6s cubic-bezier(.4,0,.2,1)' }}/>
          <circle cx={CX} cy={CY} r="7" fill={BG}/>
          <line x1={CX} y1={CY} x2={CX} y2={CY - HOUR_LEN} stroke={handColor} strokeWidth={HAND_W} strokeLinecap="round" style={{ transform: `rotate(${hourDeg}deg)`, transformOrigin: `${CX}px ${CY}px`, transition: 'transform 0.6s cubic-bezier(.4,0,.2,1)' }}/>
          <line x1={CX} y1={CY} x2={CX} y2={CY - MINUTE_LEN} stroke={handColor} strokeWidth={HAND_W} strokeLinecap="round" style={{ transform: `rotate(${minuteDeg}deg)`, transformOrigin: `${CX}px ${CY}px`, transition: 'transform 0.6s cubic-bezier(.4,0,.2,1)' }}/>
          <circle cx={CX} cy={CY} r="4" fill={handColor}/>
        </>}
        {!showHands && <circle cx={CX} cy={CY} r="5" fill={c.core}/>}
        {offline && <>
          <line x1="32" y1="18" x2="96" y2="82" stroke={BG} strokeWidth="9" strokeLinecap="round"/>
          <line x1="32" y1="18" x2="96" y2="82" stroke="#a1a1aa" strokeWidth="4" strokeLinecap="round"/>
        </>}
      </g>
    </svg>
  )
}
